import { type SpeakerControlConfiguration } from '@src/components/sidepanel/CompositionSettings/types';
import {
  Button,
  CaretDownIcon,
  CaretUpIcon,
  Dropdown,
  DropdownMenu,
  DropdownOption,
} from 'datocms-react-ui';

type SpeakerControlSelectorProps = {
  control: SpeakerControlConfiguration & { enabled?: boolean };
  onUpdateSpeaker: (id: string, type: 'name' | 'title' | 'headshot') => void;
  options: { id: string; label: string }[];
  headshotOptions: { id: string; label: string }[];
};

export function SpeakerControlSelector({
  control,
  onUpdateSpeaker,
  options,
  headshotOptions,
}: SpeakerControlSelectorProps) {
  // Find which option is currently bound to this field type
  const activeNameOptionId = Object.entries(control.controlBindings || {}).find(
    ([, value]) => value === 'name',
  )?.[0];
  const activeRoleOptionId = Object.entries(control.controlBindings || {}).find(
    ([, value]) => value === 'title',
  )?.[0];
  const activeHeadshotOptionId = Object.entries(
    control.controlBindings || {},
  ).find(([, value]) => value === 'headshot')?.[0];

  const activeNameOptionLabel =
    options.find(o => o.id === activeNameOptionId)?.label || 'None';
  const activeRoleOptionLabel =
    options.find(o => o.id === activeRoleOptionId)?.label || 'None';
  const activeHeadshotOptionLabel =
    headshotOptions.find(o => o.id === activeHeadshotOptionId)?.label || 'None';
  return (
    <>
      <Dropdown
        renderTrigger={({ open, onClick }) => (
          <Button
            buttonSize="xxs"
            type="button"
            onClick={onClick}
            rightIcon={open ? <CaretUpIcon /> : <CaretDownIcon />}
          >
            <span
              style={{ textTransform: 'capitalize' }}
            >{`Name: ${activeNameOptionLabel}`}</span>
          </Button>
        )}
      >
        <DropdownMenu>
          {options.map(o => {
            return (
              <DropdownOption
                key={o.id}
                disabled={o.id === activeRoleOptionId}
                active={activeNameOptionId === o.id}
                onClick={() => {
                  onUpdateSpeaker(o.id, 'name');
                }}
              >
                {o.label}
              </DropdownOption>
            );
          })}
          <DropdownOption
            active={activeNameOptionId === 'none'}
            onClick={() => {
              onUpdateSpeaker('none', 'name');
            }}
          >
            None
          </DropdownOption>
        </DropdownMenu>
      </Dropdown>
      <Dropdown
        renderTrigger={({ open, onClick }) => (
          <Button
            buttonSize="xxs"
            type="button"
            onClick={onClick}
            rightIcon={open ? <CaretUpIcon /> : <CaretDownIcon />}
          >
            <span
              style={{ textTransform: 'capitalize' }}
            >{`Role: ${activeRoleOptionLabel}`}</span>
          </Button>
        )}
      >
        <DropdownMenu>
          {options.map(o => {
            return (
              <DropdownOption
                key={o.id}
                disabled={o.id === activeNameOptionId}
                active={activeRoleOptionId === o.id}
                onClick={() => {
                  onUpdateSpeaker(o.id, 'title');
                }}
              >
                {o.label}
              </DropdownOption>
            );
          })}
          <DropdownOption
            active={activeRoleOptionId === 'none'}
            onClick={() => {
              onUpdateSpeaker('none', 'title');
            }}
          >
            None
          </DropdownOption>
        </DropdownMenu>
      </Dropdown>
      <Dropdown
        renderTrigger={({ open, onClick }) => (
          <Button
            buttonSize="xxs"
            type="button"
            onClick={onClick}
            rightIcon={open ? <CaretUpIcon /> : <CaretDownIcon />}
          >
            <span
              style={{ textTransform: 'capitalize' }}
            >{`Headshot: ${activeHeadshotOptionLabel}`}</span>
          </Button>
        )}
      >
        <DropdownMenu>
          {headshotOptions.map(o => {
            return (
              <DropdownOption
                key={o.id}
                active={activeHeadshotOptionId === o.id}
                onClick={() => {
                  onUpdateSpeaker(o.id, 'headshot');
                }}
              >
                {o.label}
              </DropdownOption>
            );
          })}
          <DropdownOption
            active={activeHeadshotOptionId === 'none'}
            onClick={() => {
              onUpdateSpeaker('none', 'headshot');
            }}
          >
            None
          </DropdownOption>
        </DropdownMenu>
      </Dropdown>
    </>
  );
}
